import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
  GAD7_TITLE,
  GAD7_SUBTITLE,
  GAD7_MAX_SCORE,
  GAD7_DISCLAIMER,
  GAD7_WINDOW_DAYS
} from "../../constants/gad7.js";
import { HUB_ILLUSTRATIONS, HUB_CARD_ICONS } from "../../constants/mediaAssets.js";
import HubIllustration from "../HubIllustration.js";
import MediaIcon from "../MediaIcon.js";
import MentalPatternsBackLink from "./MentalPatternsBackLink.js";
import LockedPatternTease from "./LockedPatternTease.js";
import { useMentalPatterns } from "./MentalPatternsContext.js";
import { MIN_INSIGHTS_FOR_PATTERNS } from "../../constants/mentalPatternsHub.js";

const MentalPatternsGad7 = () => {
  const { isSubscriber, verifiedEmail } = useOutletContext() || {};
  const { gad7Analysis, totalReflections, lockedPatternTease } = useMentalPatterns();
  const [historyOpen, setHistoryOpen] = useState(false);

  const {
    items,
    totalScore,
    severityLabel,
    severityNote,
    matchingInsights,
    reflectionsInWindow,
    hasData
  } = gad7Analysis;

  const showLockedTease =
    !isSubscriber && totalReflections >= MIN_INSIGHTS_FOR_PATTERNS && hasData;

  const meterPercent = totalScore != null ? Math.round((totalScore / GAD7_MAX_SCORE) * 100) : 0;

  if (!hasData) {
    return (
      <>
        <MentalPatternsBackLink />
        <div className="mental-patterns__hero">
          <HubIllustration src={HUB_ILLUSTRATIONS.gad7} alt="" className="hub-illustration hub-illustration--gad7" />
          <h1>{GAD7_TITLE}</h1>
        </div>
        <p>
          No anxiety-related reflections in the past {GAD7_WINDOW_DAYS} days yet. Patterns appear when reflections show
          worry, tension, or related language.
        </p>
      </>
    );
  }

  if (showLockedTease) {
    return (
      <>
        <MentalPatternsBackLink />
        <div className="mental-patterns__hero">
          <HubIllustration src={HUB_ILLUSTRATIONS.gad7} alt="" className="hub-illustration hub-illustration--gad7" />
          <h1>{GAD7_TITLE}</h1>
        </div>
        <LockedPatternTease tease={lockedPatternTease} verifiedEmail={verifiedEmail} />
      </>
    );
  }

  return (
    <>
      <MentalPatternsBackLink />
      <div className="mental-patterns__hero">
        <HubIllustration src={HUB_ILLUSTRATIONS.gad7} alt="" className="hub-illustration hub-illustration--gad7" />
        <h1>{GAD7_TITLE}</h1>
        <p className="gad7-patterns__subtitle">{GAD7_SUBTITLE}</p>
      </div>

      <section className="gad7-patterns" aria-label="GAD-7 pattern signal">
        <h2 className="gad7-patterns__heading">Anxiety pattern signal</h2>
        <p className="gad7-patterns__intro">
          Categories scored from confirmed emotions, confirmed thinking patterns, and reflection text across{" "}
          {reflectionsInWindow} reflection{reflectionsInWindow !== 1 ? "s" : ""} in the past {GAD7_WINDOW_DAYS} days.
          Unconfirmed Quick Clarity emotions and patterns are not counted.
        </p>

        <div className="gad7-scale">
          <div className="gad7-scale__labels">
            <span>Minimal</span>
            <span>Severe</span>
          </div>
          <div
            className="gad7-scale__track"
            role="meter"
            aria-valuenow={totalScore}
            aria-valuemin={0}
            aria-valuemax={GAD7_MAX_SCORE}
            aria-label={`${GAD7_TITLE}: ${totalScore} out of ${GAD7_MAX_SCORE}, ${severityLabel}`}
          >
            <div className="gad7-scale__gradient" />
            <div
              className="gad7-scale__marker"
              style={{ left: `${meterPercent}%` }}
              title={`${totalScore}/${GAD7_MAX_SCORE}`}
            />
          </div>
          <p className="gad7-scale__score">
            <strong>{totalScore}</strong>
            <span className="gad7-scale__score-max"> / {GAD7_MAX_SCORE}</span>
            <span className="gad7-scale__score-label"> — {severityLabel}</span>
          </p>
        </div>

        {severityNote ? <p className="gad7-patterns__elevated-note">{severityNote}</p> : null}

        <ol className="gad7-patterns__items" aria-label="GAD-7 symptom breakdown">
          {items.map((item, index) => (
            <li key={item.id} className="gad7-patterns__item">
              <div className="gad7-patterns__item-head">
                <span className="gad7-patterns__item-num">{index + 1}</span>
                <div>
                  <p className="gad7-patterns__item-symptom">{item.symptom}</p>
                  <p className="gad7-patterns__item-meta">
                    {item.frequencyLabel}
                    {item.hits > 0 ? ` · ${item.hits} reflection${item.hits !== 1 ? "s" : ""}` : ""}
                  </p>
                </div>
                <span className="gad7-patterns__item-score" aria-label={`Score ${item.score} out of 3`}>
                  {item.score}
                </span>
              </div>
            </li>
          ))}
        </ol>

        <p className="gad7-patterns__disclaimer">{GAD7_DISCLAIMER}</p>
      </section>

      <section className="mental-patterns__history mental-patterns__collapsible" aria-label="Matching reflections">
        <button
          type="button"
          className="mental-patterns__collapsible-trigger"
          aria-expanded={historyOpen}
          aria-controls="gad7-reflections-list"
          onClick={() => setHistoryOpen((open) => !open)}
        >
          <h2>Matching reflections</h2>
          <span className="mental-patterns__collapsible-count">{matchingInsights.length}</span>
          <span className="mental-patterns__collapsible-icon" aria-hidden>
            {historyOpen ? "−" : "+"}
          </span>
        </button>
        {historyOpen ? (
          <ul id="gad7-reflections-list" className="mental-patterns__history-list">
            {matchingInsights.map((entry) => (
              <li key={entry.id} className="mental-patterns__history-item">
                <MediaIcon
                  iconKey={HUB_CARD_ICONS.gad7}
                  className="media-icon mental-patterns__history-art"
                  size={24}
                  alt=""
                />
                <div>
                  <p className="mental-patterns__history-meta">
                    {new Date(entry.date).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                      year: "numeric"
                    })}
                    {entry.emotion ? ` · ${entry.emotion}` : ""}
                  </p>
                  {entry.preview ? (
                    <p className="mental-patterns__history-preview">{entry.preview}…</p>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        ) : null}
      </section>
    </>
  );
};

export default MentalPatternsGad7;
